/**
 * Permission Hook
 * 
 * Provides role-based permission checks for organization members.
 * Permissions are determined by the user's organization role:
 * - owner: Full administrative control
 * - super_user: Procurement power without administrative access
 * - member: Personal workspace with job creation rights
 */

import { useAuth } from '@/lib/auth';

export const usePermissions = () => {
  const { user } = useAuth();
  
  // Determine if user is a vendor or client based on user.role
  const isVendorUser = user?.role === 'vendor';
  const isClientUser = user?.role === 'client';
  
  return {
    // ============================================================================
    // CLIENT PERMISSIONS
    // ============================================================================
    
    // Can view and manage all jobs in the organization
    canManageAllJobs: (user?.orgRole === 'owner' || user?.orgRole === 'super_user') && isClientUser,
    
    // Can manage payment methods (stripe, etc.) - both clients and vendors need this
    canManagePayments: user?.orgRole === 'owner' || user?.orgRole === 'super_user',
    
    // Can update organization settings
    canManageSettings: user?.orgRole === 'owner',
    
    // Can invite/remove members and change roles
    canManageMembers: user?.orgRole === 'owner',
    
    // Can award contracts - owners/super users can award any, members can award their own jobs
    canAwardJob: (jobCreatorId?: string) => {
      if (!user || !isClientUser) return false;
      if (user.orgRole === 'owner' || user.orgRole === 'super_user') return true;
      if (user.orgRole === 'member' && jobCreatorId) return jobCreatorId === user.id;
      return false;
    },
    
    // Can manage a specific job (owners/super users can manage all, members only their own)
    canManageJob: (jobCreatorId?: string) => {
      if (!user || !isClientUser) return false;
      if (user.orgRole === 'owner' || user.orgRole === 'super_user') return true;
      if (user.orgRole === 'member' && jobCreatorId) return jobCreatorId === user.id;
      return false;
    },
    
    // All client members can create jobs
    canCreateJobs: !!user && isClientUser,
    
    // ============================================================================
    // VENDOR PERMISSIONS
    // ============================================================================
    
    // Can edit vendor company profile (certifications, capabilities, business info)
    canManageVendorProfile: user?.orgRole === 'owner' && isVendorUser,
    
    // Can view vendor profile (all roles can view)
    canViewVendorProfile: isVendorUser && !!user,
    
    // Can manage all organization bids (owners and super users)
    canManageAllBids: (user?.orgRole === 'owner' || user?.orgRole === 'super_user') && isVendorUser,
    
    // Can manage a specific bid (owners/super users can manage all, members only their own)
    canManageBid: (bidCreatorId?: string) => {
      if (!user || !isVendorUser) return false;
      if (user.orgRole === 'owner' || user.orgRole === 'super_user') return true;
      if (user.orgRole === 'member' && bidCreatorId) return bidCreatorId === user.id;
      return false;
    },
    
    // All vendor members can submit new bids
    canSubmitBids: isVendorUser && !!user,
    
    // Can manage vendor certifications (only owners)
    canManageVendorCertifications: user?.orgRole === 'owner' && isVendorUser,
    
    // Can manage vendor capabilities (only owners)
    canManageVendorCapabilities: user?.orgRole === 'owner' && isVendorUser,
    
    // Can manage vendor gallery (only owners)
    canManageVendorGallery: user?.orgRole === 'owner' && isVendorUser,
    
    // Can request verification (only owners)
    canRequestVerification: user?.orgRole === 'owner' && isVendorUser,
    
    // Can view all org jobs (owners and super users see all, members see only jobs they won)
    canViewAllOrgJobs: (user?.orgRole === 'owner' || user?.orgRole === 'super_user') && isVendorUser,
    
    // ============================================================================
    // ROLE CHECKS
    // ============================================================================
    
    isOwner: user?.orgRole === 'owner',
    isSuperUser: user?.orgRole === 'super_user',
    isMember: user?.orgRole === 'member',
    isVendor: isVendorUser,
    isClient: isClientUser,
  };
};
